import { getShiftPlanSettings, setShiftPlanSettings, getUserLoginFromParent } from '../../handler/parentDataHandler';
import { getStationCode } from '../../utils/networkUtil';

const CONFIG_TYPE = 'AUTOMATION_CONFIG';
const TIME_DISTRIBUTIONS_CONFIG_TYPE = 'TIME_DISTRIBUTIONS';

const queryParams = (configType = CONFIG_TYPE) => `stationCode=${getStationCode()}&configType=${configType}`;

// Reads the station TIME_DISTRIBUTIONS file. Null when absent or empty, which is how
// callers know no planning windows are configured.
export async function readStationPlanningWindows() {
  try {
    const response = await getShiftPlanSettings({
      operationType: 'get',
      queryParams: queryParams(TIME_DISTRIBUTIONS_CONFIG_TYPE)
    });
    if (response?.status !== 200 || !response?.data?.url) return null;
    const s3Response = await fetch(response.data.url);
    if (s3Response.status !== 200) return null;
    const file = await s3Response.json();
    if (!Array.isArray(file?.intervals) || file.intervals.length === 0) return null;
    return file;
  } catch (e) {
    console.warn('Flow Planner - failed to read station TIME_DISTRIBUTIONS:', e);
    return null;
  }
}

// Reads the station AUTOMATION_CONFIG via presigned URL (two-step, as Time
// Distributions). Null when there is no usable file; callers hide the section.
export async function readStationAutomationConfig() {
  const miss = (reason, detail) => {
    console.info(`Flow Planner — no station AUTOMATION_CONFIG: ${reason}`, detail ?? '');
    return null;
  };

  const response = await getShiftPlanSettings({ operationType: 'get', queryParams: queryParams() });
  if (response?.status !== 200) return miss('SPCS did not return a presigned URL', response?.status);
  if (!response?.data?.url) return miss('SPCS response had no url', response?.data);

  const s3Response = await fetch(response.data.url);
  if (s3Response.status !== 200) return miss('file not in S3 (never written yet)', s3Response.status);

  const file = await s3Response.json();
  if (!Array.isArray(file?.executionFlows) || file.executionFlows.length === 0) {
    return miss('file has no executionFlows', Object.keys(file || {}));
  }
  return file;
}

// Writes the station AUTOMATION_CONFIG. `file` must be the full body: a presigned
// PUT replaces the whole object, so omitted keys are lost.
export async function writeStationAutomationConfig(file) {
  const response = await setShiftPlanSettings({ operationType: 'upload', queryParams: queryParams() });
  if (response?.status !== 200 || !response?.data?.url) {
    throw new Error('Could not get an upload URL for the automation config.');
  }

  const userLogin = await getUserLoginFromParent()
    .then((r) => r.name || 'unknown')
    .catch(() => 'unknown');

  const body = JSON.stringify({
    ...file,
    lastUpdatedBy: userLogin,
    lastUpdatedAt: String(Math.round(Date.now() / 1000))
  });

  const putResponse = await fetch(response.data.url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body
  });
  if (!putResponse.ok) throw new Error('Failed to save the automation config.');
}
